/**
 * How an appointment block in the admin calendar lays itself out.
 *
 * Everything keys off the block's rendered height in pixels rather than the
 * appointment's length: with the hour-row height configurable, a 50-minute visit is
 * 47px on the densest setting and 167px on the roomiest, and it should be laid out
 * accordingly. The thresholds are chosen so the default 80px row reproduces the
 * original look exactly (1h = full layout, 30min = one line, no time), and taller rows
 * progressively earn more detail.
 *
 * Lives here so the calendar and the live preview in the scheduling settings render the
 * same block for the same height — the preview is the only way an owner sees what a row
 * height buys them before saving.
 */

/** Below this an event is a sliver — any text would just be clipped. */
export const EVENT_TEXT_MIN_PX = 13;
/** Below this the type has to be at its smallest, and the time range doesn't fit. */
export const EVENT_TIGHT_PX = 42;
/** At or above this the name and the service get a line each. */
export const EVENT_ROOMY_PX = 78;
/** A block this tall carries the name at full size. */
export const EVENT_LARGE_PX = 160;
/** The roomiest tier: the name goes up another notch and the service follows it. */
export const EVENT_XL_PX = 220;

export type CalendarViewKey = 'day' | 'week' | 'month' | string;

export interface ICalendarEventLayout {
  /** The block has room for any text at all. */
  showText: boolean;
  /** Name and service each get their own line (as opposed to "name - service"). */
  roomy: boolean;
  /** The block is short enough to need the tighter padding. */
  tight: boolean;
  /** The time range fits under the text. */
  showTimeRange: boolean;
  /** Tailwind type scale for the customer name. */
  primaryTextClass: string;
  /** Tailwind type scale for the service line. */
  secondaryTextClass: string;
}

/**
 * @param heightPx  The block's rendered height (duration in hours × row height).
 * @param view      Which calendar view is rendering it.
 */
export function calendarEventLayout(heightPx: number, view: CalendarViewKey = 'day'): ICalendarEventLayout {
  const isWeek = view === 'week';

  // The weekly view stays a notch smaller whatever the row height — seven columns
  // across a phone leave no width, however much height there is.
  let primaryTextClass: string;
  if (isWeek) {
    primaryTextClass = heightPx < EVENT_TIGHT_PX ? 'text-[9px]' : 'text-[11px]';
  } else if (heightPx < EVENT_TIGHT_PX) {
    primaryTextClass = 'text-[12px]';
  } else if (heightPx < EVENT_ROOMY_PX) {
    primaryTextClass = 'text-xs';
  } else if (heightPx < EVENT_LARGE_PX) {
    primaryTextClass = 'text-sm';
  } else if (heightPx < EVENT_XL_PX) {
    primaryTextClass = 'text-base';
  } else {
    primaryTextClass = 'text-lg';
  }

  let secondaryTextClass: string;
  if (isWeek) {
    secondaryTextClass = 'text-[10px] line-clamp-3';
  } else if (heightPx >= EVENT_XL_PX) {
    secondaryTextClass = 'text-base truncate';
  } else if (heightPx >= EVENT_LARGE_PX) {
    secondaryTextClass = 'text-sm truncate';
  } else {
    secondaryTextClass = 'text-xs truncate';
  }

  return {
    showText: heightPx >= EVENT_TEXT_MIN_PX,
    roomy: heightPx >= EVENT_ROOMY_PX,
    tight: heightPx < EVENT_TIGHT_PX,
    // The month view gives the space to the name/service instead.
    showTimeRange: view !== 'month' && heightPx >= EVENT_TIGHT_PX,
    primaryTextClass,
    secondaryTextClass,
  };
}
